import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const wrap = "min-h-[70vh] flex items-center justify-center";
const box = "w-full max-w-md bg-white border border-pslborder rounded-sm p-8";
const inp = "w-full border border-pslborder rounded-sm p-3 text-sm focus:outline-none focus:border-gold mb-3";
const btn = "w-full py-3 bg-green text-cream font-bold rounded-sm hover:bg-ink transition-colors disabled:opacity-60";

export function LoginPage() {
  const { onLoggedIn } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await client.post("/auth/login", form);
      onLoggedIn(data.user, data.token);
      toast.success(`Welcome, ${data.user.username}`);
      nav(data.user.role !== "fan" ? "/admin" : "/fantasy");
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={wrap}>
      <div className={box} data-testid="login-page">
        <h1 className="font-head font-black text-2xl text-green mb-1">Sign in</h1>
        <p className="text-sm text-ink/50 mb-5">Enter your email and password</p>
        {err && <div className="bg-live/10 text-live text-sm rounded-sm p-2 mb-3" data-testid="login-error">{err}</div>}
        <input data-testid="login-email" type="email" placeholder="Email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="login-password" type="password" placeholder="Password" className={inp} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <button className={btn} onClick={submit} disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Sign in"}</button>
        <p className="text-sm text-center mt-4 text-ink/60">No account? <Link to="/signup" className="text-green font-bold">Sign up</Link></p>
      </div>
    </div>
  );
}

export function SignupPage() {
  const { onLoggedIn } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", username: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await client.post("/auth/register", form);
      onLoggedIn(data.user, data.token);
      toast.success("Welcome — your account is ready");
      nav("/fantasy");
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={wrap}>
      <div className={box} data-testid="signup-page">
        <h1 className="font-head font-black text-2xl text-green mb-1">Create your free account</h1>
        <p className="text-sm text-ink/50 mb-5">Free to read. Sign up to play fantasy, post & chat.</p>
        {err && <div className="bg-live/10 text-live text-sm rounded-sm p-2 mb-3" data-testid="signup-error">{err}</div>}
        <input data-testid="signup-email" type="email" placeholder="Email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="signup-username" placeholder="Username (public)" className={inp} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        <input data-testid="signup-phone" placeholder="Phone — any country (+263, +27, +44, +1 …)" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <p className="text-xs text-ink/50 -mt-2 mb-3">Diaspora welcome — sign up with your mobile number from anywhere in the world.</p>
        <input data-testid="signup-password" type="password" placeholder="Password" className={inp} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <button className={btn} onClick={submit} disabled={busy} data-testid="signup-submit">{busy ? "Creating…" : "Sign up"}</button>
        <p className="text-sm text-center mt-4 text-ink/60">Have an account? <Link to="/login" className="text-green font-bold">Sign in</Link></p>
      </div>
    </div>
  );
}
